import { FC, ReactNode, useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/store/auth.store'
import { internalApiClient } from '@/services/apiClient'
import Header from '@/components/Header'
import BottomNavigation from '@/components/BottomNavigation'
import LoadingView from '@/components/LoadingView'
import ErrorView from '@/components/ErrorView'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Motion } from '@/components/ui/motion'
import { toastService } from '@/services/toastService'
import { useTranslation } from '@/hooks/useTranslation'
import { ProjectPosition, useInstallerProjectPositions } from '@/hooks/useInstallerProjectPositions'
import { decodeFloor, formatFrameFinish, formatFrameModel } from '@/utils/windowPosition'
import { AlertCircle, ChevronDown, Link as LinkIcon, QrCode, Unlink } from 'lucide-react'

interface QrTag {
  id: string
  serialNo: string
  sequence: number
  year: number
  clientCode: string
  status: string
  issueDate: string
  issuer: string
  structure?: Record<string, unknown>
  data?: Record<string, unknown>
  projectId?: string | null
  assetModelId?: string
  tenantId: string
  createdAt: string
  updatedAt: string
  assetModel?: {
    id: string
    name: string
    type: string
    brand: string
    model: string
  }
}

interface Project {
  id: string
  projectNumber: string
  projectDate: string
  customerLastName: string
  customerFirstName: string
  businessName: string
  address: string
  zip: string
  city: string
  province: string
  country: string
  buildingType: string
  referenceFirstName: string | null
  referenceLastName: string | null
  email: string
  phoneNumber: string | null
  documents: unknown[]
  contractor: string | null
  status: string
  tenantId: string
  createdAt: string
  updatedAt: string
}

const cardClass = 'rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]'

const DetailRow: FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
  <div className='flex items-baseline justify-between gap-3 py-1.5'>
    <span className='text-xs text-[hsl(var(--muted-foreground))]'>{label}</span>
    <span className='text-sm font-medium text-right'>{value}</span>
  </div>
)

/**
 * The frame as the installer needs to recognise it on site: model code, model
 * description, finish and measures, all on one line.
 */
const describeFrame = (position: ProjectPosition): string =>
  [
    formatFrameModel({
      brand: position.assetModel?.brand,
      model: position.assetModel?.model,
      modelDescription: position.assetModel?.description
    }),
    formatFrameFinish({
      articleDescription: position.articleDescription,
      width: position.width,
      height: position.height
    })
  ]
    .filter(Boolean)
    .join(' - ')

/** The room number restarts on every floor, so the floor is part of its identity. */
const windowRoomKey = (position: ProjectPosition): string =>
  `${position.buildingFloor ?? ''}|${position.positionNumber}`

/**
 * A commessa carries one row per sequence, but a window room only ever gets one
 * QR tag, and the sequence itself is not meaningful to the installer.
 *
 * So the picker lists rooms, not rows: rooms that already carry a QR tag on any
 * of their sequences drop out entirely, and the remaining ones are represented
 * by their lowest sequence, which is the row the tag gets linked to.
 */
const selectableWindowRooms = (positions: ProjectPosition[]): ProjectPosition[] => {
  const linkedRooms = new Set(positions.filter(p => p.qrTagId).map(windowRoomKey))
  const byRoom = new Map<string, ProjectPosition>()

  for (const position of positions) {
    const key = windowRoomKey(position)
    if (position.qrTagId || linkedRooms.has(key)) continue

    const current = byRoom.get(key)
    if (!current || position.sequenceNumber < current.sequenceNumber) {
      byRoom.set(key, position)
    }
  }

  return Array.from(byRoom.values()).sort(
    (a, b) => a.positionNumber - b.positionNumber || a.sequenceNumber - b.sequenceNumber
  )
}

const LinkingPage: FC = () => {
  const navigate = useNavigate()
  const {
    projectId,
    positionId: pathPositionId,
    qrTagId: pathQrTagId
  } = useParams<{
    projectId: string
    positionId?: string
    qrTagId?: string
  }>()
  const [searchParams] = useSearchParams()
  const { user, isInstaller, isAuthenticated } = useAuthStore()
  const { t } = useTranslation()

  // Get optional parameters from either path or query parameters (path takes precedence)
  const positionId = pathPositionId || searchParams.get('positionId')
  const qrTagId = pathQrTagId || searchParams.get('qrTagId')

  // State
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [project, setProject] = useState<Project | null>(null)
  const [qrTags, setQrTags] = useState<QrTag[]>([])
  const [selectedPositionId, setSelectedPositionId] = useState<string | null>(positionId)
  const [selectedQrTagId, setSelectedQrTagId] = useState<string | null>(qrTagId)
  // Tracks which card triggered the unlink, so only that button shows the spinner
  const [unlinkingPositionId, setUnlinkingPositionId] = useState<string | null>(null)

  // Use the new hook for installer project positions
  const {
    positions,
    loading: positionsLoading,
    error: positionsError,
    linkingError,
    isLinking,
    isUnlinking,
    fetchPositionsByProject,
    linkQrTag: linkQrTagHook,
    unlinkQrTag: unlinkQrTagHook,
    updateLocalPosition
  } = useInstallerProjectPositions()

  // Authentication check
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }

    // Check if user has installer role (optional protection)
    if (user && !isInstaller) {
      navigate('/go')
      return
    }
  }, [isAuthenticated, user, isInstaller, navigate])

  // Define loadData with useCallback to avoid dependency warning
  const loadData = useCallback(async () => {
    if (!projectId) return

    try {
      setLoading(true)
      setError(null)

      // Load project and QR tags in parallel using installer-specific endpoints
      const [projectResponse, qrTagsResponse] = await Promise.all([
        internalApiClient.get<Project>(`/installer-projects/${projectId}/details`),
        internalApiClient.get<QrTag[]>(`/installer-projects/${projectId}/qrtags`)
      ])

      setProject(projectResponse.data)
      setQrTags(qrTagsResponse.data)

      // Fetch positions using the hook
      await fetchPositionsByProject(projectId)
    } catch (err) {
      console.error('Failed to load data:', err)
      setError(err instanceof Error ? err.message : 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }, [projectId, fetchPositionsByProject])

  // Load data
  useEffect(() => {
    if (!projectId) {
      setError('Project ID is required')
      setLoading(false)
      return
    }

    loadData()
  }, [projectId, loadData])

  // Effect to auto-select QR tag when data is loaded (if qrTagId is a serialNo)
  useEffect(() => {
    if (qrTags.length > 0 && qrTagId) {
      // Try to find QR tag by ID first, then by serialNo
      let targetQrTag = qrTags.find(tag => tag.id === qrTagId)
      if (!targetQrTag) {
        // If not found by ID, try to find by serialNo
        targetQrTag = qrTags.find(tag => tag.serialNo === qrTagId)
      }

      if (targetQrTag) {
        setSelectedQrTagId(targetQrTag.id)
      }
    }
  }, [qrTags, qrTagId, selectedQrTagId])

  const handleLink = async () => {
    if (!selectedPositionId || !selectedQrTagId) {
      const errorMessage = t('LINKING_SELECT_BOTH_POSITION_AND_QR_TAG')
      toastService.warning(errorMessage)
      return
    }

    // Use the hook to link QR tag
    const success = await linkQrTagHook(selectedPositionId, selectedQrTagId)

    if (success) {
      // Update local state to reflect the linked QR tag
      const selectedQrTag = qrTags.find(tag => tag.id === selectedQrTagId)
      if (selectedQrTag) {
        updateLocalPosition(selectedPositionId, {
          qrTagId: selectedQrTagId,
          qrTag: {
            id: selectedQrTag.id,
            serialNo: selectedQrTag.serialNo,
            sequence: selectedQrTag.sequence,
            year: selectedQrTag.year,
            clientCode: selectedQrTag.clientCode,
            status: selectedQrTag.status
          }
        })
      }

      // Clear selections after successful link
      setSelectedPositionId(null)
      setSelectedQrTagId(null)
    }
  }

  const handleUnlink = async (position: ProjectPosition) => {
    setUnlinkingPositionId(position.id)

    try {
      // Use the hook to unlink QR tag
      const success = await unlinkQrTagHook(position.id)

      if (success) {
        // Update local state to reflect the unlinked QR tag
        updateLocalPosition(position.id, {
          qrTagId: null,
          qrTag: undefined
        })
      }
    } finally {
      setUnlinkingPositionId(null)
    }
  }

  if (loading || positionsLoading) {
    return <LoadingView message={t('LINKING_LOADING_DATA')} />
  }

  if (error || positionsError) {
    return <ErrorView message={error || positionsError || 'Unknown error'} />
  }

  if (!project) {
    return <ErrorView message={t('LINKING_PROJECT_NOT_FOUND')} />
  }

  // Filter QR tags - show unassigned ones (not linked to any position)
  const positionsWithQrTags = positions.filter(position => position.qrTag)
  const unassignedQrTags = qrTags.filter(tag => !positions.some(position => position.qrTagId === tag.id))

  // Window rooms still waiting for a QR tag, one entry per room
  const unassignedPositions = selectableWindowRooms(positions)

  const selectedPosition = positions.find(p => p.id === selectedPositionId)
  const selectedQrTag = qrTags.find(q => q.id === selectedQrTagId)

  return (
    <div className='min-h-screen bg-[hsl(var(--background))] pb-24'>
      {/* Back to the window room list of the project being linked */}
      <Header
        title={t('LINKING_PAGE_TITLE')}
        backTo={projectId ? `/project/${projectId}` : undefined}
        showBack
      />

      <div className='container mx-auto px-4 py-6'>
        <Motion variant='fadeIn' duration={500}>
          {/* Project Header with Client Information */}
          <div className='mb-6'>
            <h1 className='text-2xl font-semibold tracking-tight'>{project.projectNumber}</h1>
            <p className='text-sm text-[hsl(var(--muted-foreground))] mt-1'>{project.businessName}</p>
            <p className='text-sm text-[hsl(var(--muted-foreground))]'>
              {project.address}, {project.city}
            </p>

            {/* Client Information Section */}
            <div className={`${cardClass} mt-4`}>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 p-5'>
                <div>
                  <p className='text-xs text-[hsl(var(--muted-foreground))]'>
                    {t('QR_MANAGEMENT_COMMITTENTE_LABEL')}
                  </p>
                  <p className='text-sm font-medium mt-1'>
                    {project.contractor ? project.contractor : t('QR_MANAGEMENT_NO_COMMITTENTE')}
                  </p>
                </div>
                <div>
                  <p className='text-xs text-[hsl(var(--muted-foreground))]'>
                    {t('QR_MANAGEMENT_CLIENTE_FINALE_LABEL')}
                  </p>
                  <p className='text-sm font-medium mt-1'>
                    {project.customerFirstName && project.customerLastName
                      ? `${project.customerFirstName} ${project.customerLastName}`
                      : t('QR_MANAGEMENT_NO_CLIENTE_FINALE')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Show selection interface only if there are items to link */}
          {(unassignedPositions.length > 0 || unassignedQrTags.length > 0) && (
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
              {/* Window Room Selection */}
              {unassignedPositions.length > 0 && (
                <div className={cardClass}>
                  <div className='p-5'>
                    <div className='flex items-center justify-between gap-2 mb-4'>
                      <h2 className='text-lg font-semibold'>{t('LINKING_SELECT_POSITION')}</h2>
                      {selectedPosition && (
                        <span className='inline-flex items-center rounded-full bg-[hsl(var(--primary))] px-2.5 py-0.5 text-xs font-medium text-white'>
                          {t('LINKING_SELECTED')}
                        </span>
                      )}
                    </div>

                    <div className='flex flex-col gap-2'>
                      <Label htmlFor='position-select'>{t('LINKING_PROJECT_POSITIONS')}</Label>
                      <div className='relative'>
                        <Select
                          id='position-select'
                          fullWidth
                          className='pr-9 bg-[hsl(var(--card))]'
                          value={selectedPositionId || ''}
                          onChange={e => setSelectedPositionId(e.target.value || null)}
                        >
                          <option value=''>{t('LINKING_CHOOSE_POSITION')}</option>
                          {unassignedPositions.map(position => (
                            <option key={position.id} value={position.id}>
                              {position.positionNumber} - {position.description}
                              {position.buildingFloor && ` (${decodeFloor(position.buildingFloor)})`}
                            </option>
                          ))}
                        </Select>
                        <ChevronDown className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]' />
                      </div>
                    </div>

                    {selectedPosition && (
                      <div className='mt-4 rounded-[var(--radius)] bg-[hsl(var(--muted))] p-4'>
                        <h3 className='text-sm font-semibold mb-2'>{t('LINKING_POSITION_DETAILS')}</h3>
                        <div className='divide-y divide-[hsl(var(--border))]'>
                          <DetailRow label={t('LINKING_POSITION_NUMBER')} value={selectedPosition.positionNumber} />
                          {selectedPosition.buildingFloor && (
                            <DetailRow
                              label={t('LINKING_FLOOR')}
                              value={decodeFloor(selectedPosition.buildingFloor)}
                            />
                          )}
                          <DetailRow label={t('LINKING_DESCRIPTION')} value={selectedPosition.description} />
                          {describeFrame(selectedPosition) && (
                            <DetailRow label={t('LINKING_ASSET')} value={describeFrame(selectedPosition)} />
                          )}
                          {selectedPosition.notes && (
                            <DetailRow label={t('LINKING_NOTES')} value={selectedPosition.notes} />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* QR Tag Selection */}
              {unassignedQrTags.length > 0 && (
                <div className={cardClass}>
                  <div className='p-5'>
                    <div className='flex items-center justify-between gap-2 mb-4'>
                      <h2 className='text-lg font-semibold'>{t('LINKING_SELECT_QR_TAG')}</h2>
                      {selectedQrTag && (
                        <span className='inline-flex items-center rounded-full bg-[hsl(var(--primary))] px-2.5 py-0.5 text-xs font-medium text-white'>
                          {t('LINKING_SELECTED')}
                        </span>
                      )}
                    </div>

                    <div className='flex flex-col gap-2'>
                      <Label htmlFor='qrtag-select'>{t('LINKING_AVAILABLE_QR_TAGS')}</Label>
                      <div className='relative'>
                        <Select
                          id='qrtag-select'
                          fullWidth
                          className='pr-9 bg-[hsl(var(--card))]'
                          value={selectedQrTagId || ''}
                          onChange={e => setSelectedQrTagId(e.target.value || null)}
                        >
                          <option value=''>{t('LINKING_CHOOSE_QR_TAG')}</option>
                          {unassignedQrTags.map(qrTag => (
                            <option key={qrTag.id} value={qrTag.id}>
                              {qrTag.serialNo}
                              {qrTag.assetModel && ` (${qrTag.assetModel.name})`}
                            </option>
                          ))}
                        </Select>
                        <ChevronDown className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]' />
                      </div>
                    </div>

                    {selectedQrTag && (
                      <div className='mt-4 rounded-[var(--radius)] bg-[hsl(var(--muted))] p-4'>
                        <h3 className='text-sm font-semibold mb-2'>{t('LINKING_QR_TAG_DETAILS')}</h3>
                        <div className='divide-y divide-[hsl(var(--border))]'>
                          <DetailRow label={t('LINKING_SERIAL_NO')} value={selectedQrTag.serialNo} />
                          <DetailRow label={t('LINKING_YEAR')} value={selectedQrTag.year} />
                          <DetailRow label={t('LINKING_CLIENT_CODE')} value={selectedQrTag.clientCode} />
                          {selectedQrTag.assetModel && (
                            <DetailRow
                              label={t('LINKING_ASSET_MODEL')}
                              value={`${selectedQrTag.assetModel.brand} ${selectedQrTag.assetModel.model}`}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons - only show if both unassigned positions and QR tags are available */}
          {unassignedPositions.length > 0 && unassignedQrTags.length > 0 && (
            <div className='mt-6 flex justify-center'>
              <Button
                size='lg'
                onClick={handleLink}
                loading={isLinking}
                disabled={!selectedPositionId || !selectedQrTagId}
                className='w-full sm:w-auto'
              >
                <LinkIcon className='h-5 w-5' />
                {t('LINKING_LINK_QR_TAG_TO_POSITION')}
              </Button>
            </div>
          )}

          {linkingError && (
            <div className='mt-4 flex items-start gap-3 rounded-[var(--radius)] border border-[hsl(var(--destructive))]/30 bg-[hsl(var(--destructive))]/10 p-4'>
              <AlertCircle className='h-5 w-5 shrink-0 text-[hsl(var(--destructive))]' />
              <span className='text-sm text-[hsl(var(--destructive))]'>{linkingError}</span>
            </div>
          )}

          {/* Window Rooms with Linked QR Tags */}
          {positionsWithQrTags.length > 0 && (
            <div className='mt-10'>
              <h2 className='text-xl font-semibold mb-4'>
                {t('LINKING_POSITIONS_WITH_LINKED_QR_TAGS')} ({positionsWithQrTags.length})
              </h2>
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
                {positionsWithQrTags.map((position, index) => (
                  <Motion key={position.id} variant='slideUp' duration={300} delay={index * 60}>
                    <div className={`${cardClass} flex h-full flex-col`}>
                      <div className='flex flex-1 flex-col p-5'>
                        <h3 className='text-base font-semibold'>
                          {t('LINKING_POSITION_NUMBER')} {position.positionNumber}
                        </h3>

                        <div className='mt-3 divide-y divide-[hsl(var(--border))]'>
                          {position.buildingFloor && (
                            <DetailRow label={t('LINKING_FLOOR')} value={decodeFloor(position.buildingFloor)} />
                          )}
                          <DetailRow label={t('LINKING_DESCRIPTION')} value={position.description} />
                          {describeFrame(position) && (
                            <DetailRow label={t('LINKING_ASSET')} value={describeFrame(position)} />
                          )}
                        </div>

                        {position.qrTag && (
                          <div className='mt-4 rounded-[var(--radius)] bg-[hsl(var(--muted))] p-4'>
                            <div className='flex items-center gap-2 mb-1'>
                              <QrCode className='h-4 w-4 text-[var(--brand-red)]' />
                              <h4 className='text-sm font-semibold'>{t('LINKING_LINKED_QR_TAG')}</h4>
                            </div>
                            <div className='divide-y divide-[hsl(var(--border))]'>
                              <DetailRow label={t('LINKING_SERIAL_NO')} value={position.qrTag.serialNo} />
                            </div>
                          </div>
                        )}

                        <div className='mt-auto flex flex-wrap gap-2 pt-4'>
                          <Button
                            size='sm'
                            className='flex-1'
                            onClick={() => navigate(`/view/${position.qrTag?.serialNo}`)}
                            disabled={!position.qrTag?.serialNo}
                          >
                            {t('LINKING_MANAGE')}
                          </Button>
                          <Button
                            size='sm'
                            variant='outline'
                            className='flex-1 border-[hsl(var(--destructive))]/40 text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10'
                            onClick={() => handleUnlink(position)}
                            loading={unlinkingPositionId === position.id}
                            disabled={isUnlinking}
                          >
                            <Unlink className='h-4 w-4' />
                            {t('LINKING_UNLINK_QR_TAG')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Motion>
                ))}
              </div>
            </div>
          )}
        </Motion>
      </div>

      <BottomNavigation projectId={projectId} />
    </div>
  )
}

export default LinkingPage
